import {
  queryOptions,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchSupplierSuggestions } from './fetchSupplierSuggestions';
import { type SupplierSuggestionsResponse } from './types';

export type { SupplierSuggestionsResponse } from './types';

export type SupplierSuggestionsQueryParams = {
  limit?: number;
  query?: string;
};

/**
 * Generates query options for supplier suggestions, shared by the hook and
 * query-client consumers such as async select fields.
 *
 * @param params - Optional typeahead query and result limit.
 * @param options - Additional options for the query.
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_supplier_suggestions_delivery_note_suggestions_suppliers_get
 */
export const getDeliveryNoteSupplierSuggestionsQueryOptions = <
  TData = SupplierSuggestionsResponse,
>({
  params,
  options,
}: {
  params: SupplierSuggestionsQueryParams;
  options?: Omit<
    UseQueryOptions<SupplierSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchSupplierSuggestions(params, { signal }),
    queryKey: queryKeysDeliveryNote.getSupplierSuggestions(params),
    ...options,
  });

/**
 * React Query hook for supplier suggestions from previous delivery notes.
 *
 * @param params - Optional typeahead query and result limit.
 * @param options - Additional options for the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_supplier_suggestions_delivery_note_suggestions_suppliers_get
 */
// fallow-ignore-next-line unused-export -- hook is part of the endpoint's public API per the tanstack-query-hooks skill
export const useQueryDeliveryNoteSupplierSuggestions = <
  TData = SupplierSuggestionsResponse,
>(
  params: SupplierSuggestionsQueryParams,
  options?: Omit<
    UseQueryOptions<SupplierSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getDeliveryNoteSupplierSuggestionsQueryOptions<TData>({ options, params }),
  );
